import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { formatChartTitle } from "../../utils";

interface GaugeChartProps {
  value: number;
  min?: number;
  max?: number;
  baseline?: { min: number; max: number };
  unit?: string;
  height?: number | string;
  title?: string;
  useNeutralColors?: boolean;
}

const SEGMENTS = [
  { start: 0, end: 15 },
  { start: 15, end: 35 },
  { start: 35, end: 65 },
  { start: 65, end: 85 },
  { start: 85, end: 100 },
];

const START_ANGLE = -90;
const END_ANGLE = 90;

export const GaugeChart: React.FC<GaugeChartProps> = ({
  value,
  min = 0,
  max = 100,
  baseline,
  unit,
  height = "100%",
  title,
  useNeutralColors = false,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);
  const formattedTitle = formatChartTitle(title);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const normalizedValue = Math.min(Math.max((value - min) / (max - min), 0), 1);
  const chartHeight = typeof height === "number" ? height : 300;

  const padding = 20;
  const availableWidth = width ? Math.max(0, width - padding) : chartHeight;
  const outerRadius = Math.min(availableWidth / 2, chartHeight);
  const innerRadius = outerRadius * 0.85;
  const arcCenterRadius = (outerRadius + innerRadius) / 2;
  const indicatorSize = ((outerRadius - innerRadius) / 2) * 0.7;

  const getIndicatorColor = (pct: number) => {
    if (useNeutralColors) {
      if (pct <= 50) return theme.palette.primary.dark;
      return theme.palette.primary.light;
    }
    if (pct <= 15) return theme.palette.error.dark;
    if (pct <= 35) return theme.palette.warning.dark;
    if (pct <= 65) return theme.palette.success.dark;
    if (pct <= 85) return theme.palette.warning.main;
    return theme.palette.error.dark;
  };

  const totalAngle = END_ANGLE - START_ANGLE;
  const indicatorAngle = START_ANGLE + normalizedValue * totalAngle;
  const indicatorColor = getIndicatorColor(normalizedValue * 100);

  const pieData = SEGMENTS.map((segment, index) => ({
    id: index,
    value: segment.end - segment.start,
  }));

  const segmentColors = useNeutralColors
    ? [
        theme.palette.primary.dark,
        theme.palette.primary.main,
        theme.palette.primary.main,
        theme.palette.primary.main,
        theme.palette.primary.light,
      ]
    : [
        theme.palette.error.main,
        theme.palette.warning.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
      ];

  const chartWidth = outerRadius * 2;
  const svgHeight = outerRadius;
  const centerY = `calc(50% + ${svgHeight / 2}px)`;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: chartHeight,
          position: "relative",
        }}
      >
        {width > 0 && (
          <>
            <Box
              sx={{
                position: "absolute",
                top: `calc(50% - ${svgHeight / 2}px)`,
                left: "50%",
                transform: "translateX(-50%)",
                width: chartWidth,
                height: svgHeight,
                overflow: "hidden",
                zIndex: 1,
              }}
            >
              <PieChart
                series={[
                  {
                    data: pieData,
                    innerRadius,
                    outerRadius,
                    startAngle: START_ANGLE,
                    endAngle: END_ANGLE,
                    paddingAngle: 2,
                    cornerRadius: 8,
                    cx: outerRadius,
                    cy: svgHeight,
                  },
                ]}
                colors={segmentColors}
                width={chartWidth}
                height={svgHeight}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              />
            </Box>
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: centerY,
                width: indicatorSize * 2.5,
                height: indicatorSize * 2.5,
                transform: `translate(-50%, -50%) rotate(${indicatorAngle}deg) translateY(-${
                  arcCenterRadius - indicatorSize * 0.5
                }px)`,
                transformOrigin: "center center",
                pointerEvents: "none",
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  width: indicatorSize * 1.5,
                  height: indicatorSize * 1.5,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.common.white,
                  boxShadow: "0 3px 8px rgba(0, 0, 0, 0.3)",
                  outline: `${10}px solid ${indicatorColor}`,
                }}
              />
            </Box>
          </>
        )}

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            textAlign: "center",
            pt: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "center",
            }}
          >
            <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1 }}>
              {value}
            </Typography>
            {unit && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 0.5 }}
              >
                {unit}
              </Typography>
            )}
          </Box>
          {formattedTitle && (
            <Typography variant="body2" color="text.secondary">
              {formattedTitle}
            </Typography>
          )}
          {baseline && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.5, opacity: 0.7 }}
            >
              Baseline {baseline.min}-{baseline.max}ms
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
